import React from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shadcn/components/ui/select';
import { RefreshCw, History } from 'lucide-react';
import { EXECUTION_STATUSES, EXECUTION_STATUS_LABELS, TIMEFRAME_OPTIONS } from '../../constants';
import useExecutions from '../../hooks/useExecutions';
import ExecutionCard from './ExecutionCard';
import RuleSearchInput from '../Shared/RuleSearchInput';

const ExecutionList = ({ ruleOptions = [] }) => {
  const {
    executions,
    isLoading,
    filters,
    totalPages,
    currentPage,
    updateFilters,
    goToPage,
    retryExecution,
    refresh,
  } = useExecutions();

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <RuleSearchInput
          value={filters.ruleId}
          onChange={(ruleId) => updateFilters({ ruleId })}
          options={ruleOptions}
        />
        <Select
          value={filters.status || 'all'}
          onValueChange={(val) => updateFilters({ status: val === 'all' ? '' : val })}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.values(EXECUTION_STATUSES).map(status => (
              <SelectItem key={status} value={status}>
                {EXECUTION_STATUS_LABELS[status]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.timeframe || 'all'}
          onValueChange={(val) => updateFilters({ timeframe: val === 'all' ? '' : val })}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Timeframe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            {TIMEFRAME_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={refresh}>
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Execution List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <History size={48} className="mx-auto mb-4" />
          <p className="text-lg">No executions found</p>
          <p className="text-sm mt-1">Executions will appear here when rules are triggered</p>
        </div>
      ) : (
        <div className="space-y-3">
          {executions.map(execution => (
            <ExecutionCard
              key={execution._id}
              execution={execution}
              onRetry={retryExecution}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExecutionList;
